import React, { useState, useEffect } from 'react';
import {
  Building2,
  Users,
  AlertTriangle,
  Search,
  Filter,
  Eye,
  Pause,
  Play,
  Settings,
  Calendar,
  DollarSign,
  Home,
  BarChart3,
  Shield,
  LogOut,
  Plus
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PlatformSettings from './PlatformSettings';
import { TenantManageModal } from '../platform/TenantManageModal';
import PlatformNotificationConfigManagement from '../platform/NotificationConfigManagement';
import PlanList from '../platform/plans/PlanList';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { TenantActions } from '../platform/TenantActions';
import { CountryWorkflowManagement } from '../platform/CountryWorkflowManagement';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  plan: 'starter' | 'pro' | 'enterprise';
  status: 'trial' | 'active' | 'suspended';
  createdAt: string;
  userCount?: number;
  lastActivity?: string;
}

interface TenantStats {
  total: number;
  active: number;
  trial: number;
  suspended: number;
  revenue: number;
}

const planColors = {
  starter: 'bg-blue-100 text-blue-800',
  pro: 'bg-green-100 text-green-800',
  enterprise: 'bg-purple-100 text-purple-800',
};

const statusColors = {
  trial: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
  // Keep uppercase for backward compatibility
  TRIAL: 'bg-yellow-100 text-yellow-800',
  ACTIVE: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
};



// Navigation items for the sidebar
const navigationItems = [
  { id: 'dashboard', label: 'Dashboard', icon: Home },
  { id: 'tenants', label: 'Firm Management', icon: Building2 },
  { id: 'workflows', label: 'Manage Workflows', icon: Calendar },
  { id: 'plans', label: 'Plan Management', icon: BarChart3 },
  { id: 'analytics', label: 'Analytics & Reports', icon: BarChart3 },
  { id: 'users', label: 'Platform Users', icon: Users },
  { id: 'settings', label: 'Platform Settings', icon: Settings },
  { id: 'notification-config', label: 'Notification Config', icon: Settings },
  { id: 'security', label: 'Security & Logs', icon: Shield },
];

// Navigation Item Component
const NavigationItem: React.FC<{
  id: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ id, icon: Icon, label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    <Icon className="w-5 h-5 mr-3" />
    {label}
  </button>
);

const formatPlanName = (plan: string) => {
  switch (plan?.toLowerCase()) {
    case 'starter':
      return 'Starter';
    case 'pro':
      return 'Pro';
    case 'enterprise':
      return 'Enterprise';
    default:
      return plan || 'Unknown';
  }
};

// Helper function to format status names for display
const formatStatusName = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'trial':
      return 'Trial';
    case 'active':
      return 'Active';
    case 'suspended':
      return 'Suspended';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status || 'Unknown';
  }
};

// Tab Components
const TenantOverviewTab: React.FC<{ tenant: Tenant; cachedData?: any }> = ({ tenant, cachedData }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-medium text-blue-900">Total Users</h3>
        <p className="text-2xl font-bold text-blue-600">{cachedData?.overview?.userCount || cachedData?.users?.length || tenant.userCount || 1}</p>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <h3 className="font-medium text-green-900">Monthly Revenue</h3>
        <p className="text-2xl font-bold text-green-600">
          ${cachedData?.overview?.revenue?.toFixed(2) || (tenant.status === 'active' ? '50.00' : '0.00')}
        </p>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <h3 className="font-medium text-purple-900">Storage Used</h3>
        <p className="text-2xl font-bold text-purple-600">{cachedData?.overview?.storageUsed || '2.4 GB'}</p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">Recent Activity</h3>
        <div className="space-y-2 text-sm">
          {(cachedData?.overview?.activityLog || []).slice(0, 5).map((activity: any) => (
            <div key={activity.id} className="flex justify-between">
              <span>{activity.action}</span>
              <span className="text-gray-500">
                {new Date(activity.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
          {(!cachedData?.overview?.activityLog || cachedData.overview.activityLog.length === 0) && (
            <div className="text-center text-gray-500 py-2">No recent activity</div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">System Health</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Database</span>
            <span className="text-green-500">{cachedData?.overview?.systemHealth?.database || 'Healthy'}</span>
          </div>
          <div className="flex justify-between">
            <span>API Response</span>
            <span className="text-green-500">{cachedData?.overview?.systemHealth?.api || 'Normal'}</span>
          </div>
          <div className="flex justify-between">
            <span>Storage</span>
            <span className="text-yellow-500">{cachedData?.overview?.systemHealth?.storage || '75% used'}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TenantUsersTab: React.FC<{
  tenant: Tenant;
  users: any[];
  onAddUser: () => void;
  onEditUser: (id: string) => void;
  onDisableUser: (id: string) => void;
}> = ({ tenant, users, onAddUser, onEditUser, onDisableUser }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-medium">User Management ({users.length} user{users.length !== 1 ? 's' : ''})</h3>
      <button
        onClick={onAddUser}
        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
      >
        Add User
      </button>
    </div>

    <div className="bg-white border rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{user.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  (user.roles && (user.roles.includes('FIRM_ADMIN') || user.roles.includes('FirmAdmin'))) || user.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {(() => {
                    // Normalize role display
                    if (user.roles && user.roles.length > 0) {
                      return user.roles.map(role => {
                        if (role === 'FirmAdmin' || role === 'FIRM_ADMIN') return 'FirmAdmin';
                        if (role === 'Staff') return 'Staff';
                        return role;
                      }).join(', ');
                    }
                    return user.role || 'Staff';
                  })()}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  (user.status === 'ACTIVE' || user.status === 'active') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {(() => {
                    // Normalize status display
                    if (user.status === 'active') return 'active';
                    if (user.status === 'ACTIVE') return 'active';
                    return user.status || 'inactive';
                  })()}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  onClick={() => onEditUser(user.id)}
                  className="text-primary-600 hover:text-primary-900 mr-3"
                >
                  Edit Role
                </button>
                <button
                  onClick={() => onDisableUser(user.id)}
                  className={`${
                    (user.status === 'ACTIVE' || user.status === 'active') ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                  }`}
                >
                  {(user.status === 'ACTIVE' || user.status === 'active') ? 'Disable' : 'Enable'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TenantBillingTab: React.FC<{
  tenant: Tenant;
  onChangePlan: (plan: string) => void;
  onUpdatePayment: () => void;
  cachedData?: any;
}> = ({ tenant, onChangePlan, onUpdatePayment, cachedData }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Current Plan</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span>Plan</span>
            <span className="font-medium">{formatPlanName(cachedData?.billing?.plan || tenant.plan)}</span>
          </div>
          <div className="flex justify-between">
            <span>Monthly Cost</span>
            <span className="font-medium">${cachedData?.billing?.monthlyCost?.toFixed(2) || (tenant.status === 'active' ? '50.00' : '0.00')}</span>
          </div>
          <div className="flex justify-between">
            <span>Next Billing</span>
            <span className="font-medium">{cachedData?.billing?.nextBilling || 'Jan 15, 2024'}</span>
          </div>
          <div className="flex justify-between">
            <span>Status</span>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[tenant.status as keyof typeof statusColors]}`}>
              {tenant.status}
            </span>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <select
            onChange={(e) => onChangePlan(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            defaultValue={tenant.plan}
          >
            <option value="starter">Starter - $25/month</option>
            <option value="pro">Pro - $50/month</option>
            <option value="enterprise">Enterprise - $100/month</option>
          </select>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Payment Method</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-6 bg-blue-500 rounded text-white text-xs flex items-center justify-center">
              {cachedData?.billing?.paymentMethod?.type || 'VISA'}
            </div>
            <span>**** **** **** {cachedData?.billing?.paymentMethod?.last4 || '4242'}</span>
          </div>
          <div className="text-sm text-gray-500">Expires {cachedData?.billing?.paymentMethod?.expires || '12/25'}</div>
        </div>
        <button
          onClick={onUpdatePayment}
          className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Update Payment Method
        </button>
      </div>
    </div>

    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4">Billing History</h3>
      <div className="space-y-3">
        {(cachedData?.billing?.billingHistory || []).map((bill: any) => (
          <div key={bill.id} className="flex justify-between items-center py-2 border-b">
            <div>
              <div className="font-medium">{new Date(bill.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
              <div className="text-sm text-gray-500">{bill.description}</div>
            </div>
            <div className="text-right">
              <div className="font-medium">${bill.amount.toFixed(2)}</div>
              <div className={`text-sm ${bill.status === 'PAID' ? 'text-green-500' : 'text-red-500'}`}>
                {bill.status}
              </div>
            </div>
          </div>
        ))}
        {(!cachedData?.billing?.billingHistory || cachedData.billing.billingHistory.length === 0) && (
          <div className="text-center text-gray-500 py-4">No billing history available</div>
        )}
      </div>
    </div>
  </div>
);

const TenantFeaturesTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Core Features</h3>
        <div className="space-y-3">
          {[
            { name: 'User Management', enabled: true },
            { name: 'Document Storage', enabled: true },
            { name: 'Basic Reporting', enabled: true },
            { name: 'Email Support', enabled: true }
          ].map((feature, index) => (
            <div key={index} className="flex items-center justify-between">
              <span>{feature.name}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={feature.enabled} className="sr-only peer" readOnly />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Advanced Features</h3>
        <div className="space-y-3">
          {[
            { name: 'Advanced Analytics', enabled: tenant.plan === 'enterprise' },
            { name: 'API Access', enabled: tenant.plan !== 'starter' },
            { name: 'Custom Branding', enabled: tenant.plan === 'enterprise' },
            { name: 'Priority Support', enabled: tenant.plan === 'enterprise' }
          ].map((feature, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className={!feature.enabled ? 'text-gray-400' : ''}>{feature.name}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={feature.enabled} className="sr-only peer" readOnly />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const TenantSupportTab: React.FC<{
  tenant: Tenant;
  onSendMessage: () => void;
  onResetPasswords: () => void;
  onGenerateReport: () => void;
  onAccessLogs: () => void;
  cachedData?: any;
}> = ({ tenant, onSendMessage, onResetPasswords, onGenerateReport, onAccessLogs, cachedData }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Support Actions</h3>
        <div className="space-y-3">
          <button
            onClick={onSendMessage}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Send System Message
          </button>
          <button
            onClick={onResetPasswords}
            className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Reset User Passwords
          </button>
          <button
            onClick={onGenerateReport}
            className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
          >
            Generate Support Report
          </button>
          <button
            onClick={onAccessLogs}
            className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            Access Tenant Logs
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Support Statistics</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span>Open Tickets</span>
            <span className="font-medium">{cachedData?.support?.statistics?.openTickets || 2}</span>
          </div>
          <div className="flex justify-between">
            <span>Resolved This Month</span>
            <span className="font-medium">{cachedData?.support?.statistics?.resolvedThisMonth || 8}</span>
          </div>
          <div className="flex justify-between">
            <span>Avg Response Time</span>
            <span className="font-medium">{cachedData?.support?.statistics?.avgResponseTime || '2.4 hours'}</span>
          </div>
          <div className="flex justify-between">
            <span>Satisfaction Score</span>
            <span className="font-medium text-green-600">{cachedData?.support?.statistics?.satisfactionScore || 4.8}/5</span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4">Recent Support Tickets</h3>
      <div className="space-y-3">
        {(cachedData?.support?.supportTickets || []).map((ticket: any) => (
          <div key={ticket.id} className="flex justify-between items-center py-3 border-b">
            <div>
              <div className="font-medium">{ticket.title}</div>
              <div className="text-sm text-gray-500">
                Reported {new Date(ticket.createdAt).toLocaleString()}
              </div>
            </div>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              ticket.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
              ticket.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {ticket.status.replace('_', ' ')}
            </span>
          </div>
        ))}
        {(!cachedData?.support?.supportTickets || cachedData.support.supportTickets.length === 0) && (
          <div className="text-center text-gray-500 py-4">No support tickets found</div>
        )}
      </div>
    </div>

    {/* System Messages History */}
    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4">System Messages Sent</h3>
      <div className="space-y-3">
        {(cachedData?.support?.systemMessages || []).map((message: any) => (
          <div key={message.id} className="p-3 bg-blue-50 rounded-lg">
            <div className="font-medium text-blue-900">{message.message}</div>
            <div className="text-sm text-blue-600 mt-1">
              Sent to {message.recipients} users on {new Date(message.sentAt).toLocaleString()}
            </div>
          </div>
        ))}
        {(!cachedData?.support?.systemMessages || cachedData.support.systemMessages.length === 0) && (
          <div className="text-center text-gray-500 py-4">No system messages sent</div>
        )}
      </div>
    </div>
  </div>
);

const TenantAdminTab: React.FC<{
  tenant: Tenant;
  onUpdateCredentials: (data: { loginId?: string; password?: string; firstName?: string; lastName?: string }) => void;
}> = ({ tenant, onUpdateCredentials }) => {
  const [adminInfo, setAdminInfo] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [updating, setUpdating] = React.useState(false);
  const [showUpdateForm, setShowUpdateForm] = React.useState(false);
  const [updateData, setUpdateData] = React.useState({
    loginId: '',
    password: '',
    firstName: '',
    lastName: ''
  });

  // Load admin info when component mounts
  React.useEffect(() => {
    const loadAdminInfo = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/platform/tenants/${tenant.id}/admin`);
        if (response.data.success) {
          setAdminInfo(response.data.data);
          if (response.data.data) {
            setUpdateData({
              loginId: response.data.data.email,
              password: '',
              firstName: response.data.data.firstName,
              lastName: response.data.data.lastName
            });
          }
        }
      } catch (error) {
        console.error('Failed to load admin info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAdminInfo();
  }, [tenant.id]);

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const payload: any = {};
      if (updateData.loginId !== adminInfo?.email) {
        payload.loginId = updateData.loginId;
      }
      if (updateData.password) {
        payload.password = updateData.password;
      }
      if (updateData.firstName !== adminInfo?.firstName) {
        payload.firstName = updateData.firstName;
      }
      if (updateData.lastName !== adminInfo?.lastName) {
        payload.lastName = updateData.lastName;
      }

      if (Object.keys(payload).length === 0) {
        alert('No changes to update');
        return;
      }

      await onUpdateCredentials(payload);
      setShowUpdateForm(false);

      // Reload admin info
      const response = await api.get(`/platform/tenants/${tenant.id}/admin`);
      if (response.data.success) {
        setAdminInfo(response.data.data);
      }
    } catch (error) {
      console.error('Failed to update credentials:', error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading admin information...</div>
      </div>
    );
  }

  if (!adminInfo) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-yellow-800 mb-2">No Admin User Found</h3>
        <p className="text-yellow-700">
          This tenant doesn't have an admin user yet. This might happen if the tenant was created
          without admin credentials or if there was an issue during tenant creation.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Admin Info */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">Tenant Administrator</h3>
          <button
            onClick={() => setShowUpdateForm(!showUpdateForm)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            {showUpdateForm ? 'Cancel' : 'Update Credentials'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <p className="text-gray-900">{adminInfo.fullName}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (Login ID)</label>
            <p className="text-gray-900">{adminInfo.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              (adminInfo.status === 'active' || adminInfo.status === 'ACTIVE')
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {formatStatusName(adminInfo.status)}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
            <p className="text-gray-900">
              {adminInfo.lastLoginAt
                ? new Date(adminInfo.lastLoginAt).toLocaleString()
                : 'Never'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Update Form */}
      {showUpdateForm && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Update Admin Credentials</h3>
          <form onSubmit={handleUpdateSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Login ID (Email)
                </label>
                <input
                  type="email"
                  value={updateData.loginId}
                  onChange={(e) => setUpdateData(prev => ({ ...prev, loginId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={updateData.password}
                  onChange={(e) => setUpdateData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={updateData.firstName}
                  onChange={(e) => setUpdateData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={updateData.lastName}
                  onChange={(e) => setUpdateData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowUpdateForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updating}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {updating ? 'Updating...' : 'Update Credentials'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const TenantSettingsTab: React.FC<{
  tenant: Tenant;
  settings: any;
  onSettingsChange: (field: string, value: string) => void;
  onSaveSettings: () => void;
  onSuspendTenant: () => void;
  onDeleteTenant: () => void;
}> = ({ tenant, settings, onSettingsChange, onSaveSettings, onSuspendTenant, onDeleteTenant }) => (
  <div className="space-y-6">
    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4">Tenant Configuration</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Organization Name</label>
          <input
            type="text"
            value={settings.name || ''}
            onChange={(e) => onSettingsChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subdomain</label>
          <input
            type="text"
            value={settings.subdomain || ''}
            onChange={(e) => onSettingsChange('subdomain', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Plan</label>
          <select
            value={settings.plan || ''}
            onChange={(e) => onSettingsChange('plan', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="starter">Starter</option>
            <option value="pro">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={settings.status || ''}
            onChange={(e) => onSettingsChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>
      <div className="mt-6 flex space-x-3">
        <button
          onClick={onSaveSettings}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          Save Changes
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Reset
        </button>
      </div>
    </div>

    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4 text-red-600">Danger Zone</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center p-4 border border-red-200 rounded-lg">
          <div>
            <h4 className="font-medium text-red-600">Suspend Tenant</h4>
            <p className="text-sm text-gray-500">Temporarily disable access for this tenant</p>
          </div>
          <button
            onClick={onSuspendTenant}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Suspend
          </button>
        </div>
        <div className="flex justify-between items-center p-4 border border-red-200 rounded-lg">
          <div>
            <h4 className="font-medium text-red-600">Delete Tenant</h4>
            <p className="text-sm text-gray-500">Permanently delete this tenant and all data</p>
          </div>
          <button
            onClick={onDeleteTenant}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
);

export const SuperAdminDashboard: React.FC = () => {
  const { user, tenant, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<TenantStats>({
    total: 0,
    active: 0,
    trial: 0,
    suspended: 0,
    revenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [activeManageTab, setActiveManageTab] = useState('overview');
  const [managementLoading, setManagementLoading] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [systemMessage, setSystemMessage] = useState('');
  const [tenantSettings, setTenantSettings] = useState<any>({});

  // Persistent data storage for each tenant
  const [tenantDataCache, setTenantDataCache] = useState<{[key: string]: any}>({});

  useEffect(() => {
    // Check for both SuperAdmin (legacy) and SUPER_ADMIN (platform user) roles
    if (user?.roles.includes('SuperAdmin') || user?.roles.includes('SUPER_ADMIN')) {
      fetchTenants();
      fetchStats();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const fetchTenants = async () => {
    try {
      setError(null);
      setLoading(true);
      console.log('Fetching tenants...', { user: user?.roles, api: api.defaults.headers });

      // Fetch both regular tenants and platform tenants
      const [regularResponse, platformResponse] = await Promise.all([
        api.get('/tenants', {
          params: {
            search: searchTerm || undefined,
            status: statusFilter || undefined,
            limit: 50,
          },
        }),
        api.get('/platform/tenants', {
          params: {
            limit: 50,
          },
        }).catch(error => {
          console.warn('Failed to fetch platform tenants:', error);
          return { data: { success: false, data: [] } };
        })
      ]);

      console.log('Regular tenants response:', regularResponse.data);
      console.log('Platform tenants response:', platformResponse.data);

      if (regularResponse.data.success) {
        const regularTenants = regularResponse.data.data.tenants || [];

        // Create a mapping from regular tenant subdomain to platform tenant ID
        const platformTenantMap = new Map();
        if (platformResponse.data.success && platformResponse.data.data) {
          const platformTenants = Array.isArray(platformResponse.data.data) ?
            platformResponse.data.data :
            (platformResponse.data.data.tenants || []);

          platformTenants.forEach((platformTenant: any) => {
            platformTenantMap.set(platformTenant.subdomain, platformTenant.id);
          });
        }

        // Add platform tenant ID to regular tenants
        const enrichedTenants = regularTenants.map((tenant: any) => ({
          ...tenant,
          platformTenantId: platformTenantMap.get(tenant.subdomain) || null
        }));

        setTenants(enrichedTenants);
      } else {
        const errorMsg = regularResponse.data.message || 'Unknown error';
        console.error('API returned success: false', regularResponse.data);
        setError(errorMsg);
        toast.error('Failed to load tenants: ' + errorMsg);
      }
    } catch (error: any) {
      console.error('Error fetching tenants:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      });

      let errorMsg = 'Failed to load tenants';
      if (error.response?.status === 401) {
        errorMsg = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Access denied. SuperAdmin role required.';
      } else {
        errorMsg = 'Failed to load tenants: ' + (error.response?.data?.message || error.message);
      }

      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('Fetching real stats...');

      const response = await api.get('/tenants/stats');

      console.log('Stats response:', response.data);

      if (response.data.success) {
        setStats(response.data.data);
      } else {
        console.error('Stats API returned success: false', response.data);
        // Fallback to calculating from tenant list
        calculateStatsFromTenants();
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      // Fallback to calculating from tenant list
      calculateStatsFromTenants();
    }
  };

  const calculateStatsFromTenants = () => {
    const stats = {
      total: tenants.length,
      active: tenants.filter(t => t.status === 'active').length,
      trial: tenants.filter(t => t.status === 'trial').length,
      suspended: tenants.filter(t => t.status === 'suspended').length,
      revenue: tenants.filter(t => t.status === 'active').length * 50, // $50 per active tenant
    };
    setStats(stats);
  };

  const handleViewTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantModal(true);
  };

  const handleManageTenant = (tenant: Tenant) => {
    console.log('handleManageTenant called:', tenant);
    setSelectedTenant(tenant);
    setActiveManageTab('overview');
    setTenantSettings({
      name: tenant.name,
      subdomain: tenant.subdomain,
      plan: tenant.plan,
      status: tenant.status
    });

    // Always use regular tenant ID since we're using regular tenant APIs
    console.log('Managing tenant:', {
      regularTenantId: tenant.id,
      platformTenantId: (tenant as any).platformTenantId,
      usingId: tenant.id,
      subdomain: tenant.subdomain
    });

    console.log('Setting showManageModal to true');
    setShowManageModal(true);
  };

  // Load all tenant data from backend APIs
  const loadTenantData = async (tenantId: string) => {
    console.log('🔄 Loading tenant data for:', tenantId);
    console.log('🔑 Current auth state:', {
      isAuthenticated: useAuthStore.getState().isAuthenticated,
      user: useAuthStore.getState().user,
      tenant: useAuthStore.getState().tenant,
      accessToken: useAuthStore.getState().accessToken ? 'present' : 'missing'
    });
    console.log('🌐 API base URL:', api.defaults.baseURL);
    console.log('📋 API headers:', api.defaults.headers);

    try {
      // Clear cache for this tenant first
      setTenantDataCache(prev => {
        const newCache = { ...prev };
        delete newCache[tenantId];
        return newCache;
      });

      // Load users with cache-busting
      console.log('📥 Fetching users for tenant:', tenantId);
      console.log('🔗 Making API call to:', `/tenants/${tenantId}/users`);
      const usersResponse = await api.get(`/tenants/${tenantId}/users`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log('👥 Users response:', {
        status: usersResponse.status,
        success: usersResponse.data?.success,
        data: usersResponse.data?.data,
        users: usersResponse.data?.data?.users
      });

      if (usersResponse.data?.success && usersResponse.data?.data?.users) {
        console.log('✅ Setting tenant users (data.data.users):', usersResponse.data.data.users);
        setTenantUsers(usersResponse.data.data.users);

        // Update cache with fresh user data
        setTenantDataCache(prev => ({
          ...prev,
          [tenantId]: {
            ...prev[tenantId],
            users: usersResponse.data.data.users
          }
        }));
      } else if (usersResponse.data?.success && usersResponse.data?.users) {
        console.log('✅ Setting tenant users (data.users):', usersResponse.data.users);
        setTenantUsers(usersResponse.data.users);

        // Update cache with fresh user data
        setTenantDataCache(prev => ({
          ...prev,
          [tenantId]: {
            ...prev[tenantId],
            users: usersResponse.data.users
          }
        }));
      } else {
        console.warn('⚠️ Users response format unexpected:', usersResponse.data);
        console.error('❌ No users found in response');
        setTenantUsers([]);
      }

      // Load overview data with cache-busting
      console.log('📊 Fetching overview for tenant:', tenantId);
      const overviewResponse = await api.get(`/tenants/${tenantId}/overview`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log('📊 Overview response:', {
        status: overviewResponse.status,
        success: overviewResponse.data?.success,
        data: overviewResponse.data?.data,
        overview: overviewResponse.data?.data?.overview
      });

      if (overviewResponse.data?.success && overviewResponse.data?.data?.overview) {
        console.log('✅ Setting overview data (data.data.overview):', overviewResponse.data.data.overview);
        // Update cache with overview data
        setTenantDataCache(prev => ({
          ...prev,
          [tenantId]: {
            ...prev[tenantId],
            overview: overviewResponse.data.data.overview
          }
        }));
      } else if (overviewResponse.data?.success && overviewResponse.data?.overview) {
        console.log('✅ Setting overview data (data.overview):', overviewResponse.data.overview);
        // Update cache with overview data
        setTenantDataCache(prev => ({
          ...prev,
          [tenantId]: {
            ...prev[tenantId],
            overview: overviewResponse.data.overview
          }
        }));
      } else {
        console.warn('⚠️ Overview response format unexpected:', overviewResponse.data);
      }

      console.log('✅ Tenant data loading completed for:', tenantId);

      // Note: Billing and support endpoints are not yet implemented in the backend
      // They will be added when those features are developed

    } catch (error: any) {
      console.error('❌ Error loading tenant data:', {
        tenantId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config,
        code: error.code,
        stack: error.stack
      });
      console.error('❌ Full error object:', error);

      let errorMessage = 'Network Error';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Failed to load tenant data: ' + errorMessage);
    }
  };

  // Management Functions
  const loadTenantUsers = async (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) return;

    try {
      console.log('Loading users from API for tenant:', tenantId);
      const response = await api.get(`/tenants/${tenantId}/users`);

      if (!response.data.success) {
        throw new Error('Failed to load users');
      }

      console.log('Loaded users from API:', response.data.data.users);
      setTenantUsers(response.data.data.users);

      // Cache the loaded users
      setTenantDataCache(prev => ({
        ...prev,
        [tenantId]: {
          ...prev[tenantId],
          users: response.data.data.users,
          activityLog: prev[tenantId]?.activityLog || []
        }
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleAddUser = async (userData: any) => {
    if (!selectedTenant) return;

    setManagementLoading(true);
    try {
      // Transform the data to match backend expectations
      const apiData = {
        email: userData.email,
        firstName: userData.name.split(' ')[0] || userData.name,
        lastName: userData.name.split(' ').slice(1).join(' ') || '',
        roles: [userData.role], // Backend expects roles array
      };

      console.log('Adding user via API:', apiData);
      const response = await api.post(`/tenants/${selectedTenant.id}/users`, apiData);

      if (!response.data.success) {
        throw new Error('Failed to add user');
      }

      const newUser = response.data.data.user;
      console.log('User added successfully:', newUser);

      const updatedUsers = [...tenantUsers, newUser];
      setTenantUsers(updatedUsers);

      // Update cache and add activity log
      setTenantDataCache(prev => ({
        ...prev,
        [selectedTenant.id]: {
          ...prev[selectedTenant.id],
          users: updatedUsers,
          activityLog: [
            {
              id: Date.now().toString(),
              action: `User Added: ${newUser.fullName || newUser.firstName + ' ' + newUser.lastName}`,
              user: 'SuperAdmin',
              timestamp: new Date().toISOString()
            },
            ...(prev[selectedTenant.id]?.activityLog || [])
          ]
        }
      }));

      // Update tenant user count in main list
      setTenants(prev =>
        prev.map(t =>
          t.id === selectedTenant.id
            ? { ...t, userCount: updatedUsers.length }
            : t
        )
      );

      setShowAddUserModal(false);
      toast.success(`User ${userData.name} added successfully`);
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to add user');
    } finally {
      setManagementLoading(false);
    }
  };

  const handleEditUser = async (userId: string) => {
    if (!selectedTenant) return;

    const user = tenantUsers.find(u => u.id === userId);
    if (!user) return;

    // Get the user's display name
    const userName = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';

    // Check current role - handle both old format (role) and new format (roles array)
    const currentRoles = user.roles || (user.role ? [user.role] : ['Staff']);
    const hasAdminRole = currentRoles.includes('FIRM_ADMIN') || currentRoles.includes('FirmAdmin') || currentRoles.includes('ADMIN');

    // Toggle between FirmAdmin and Staff (backend expects exact enum values)
    const newRole = hasAdminRole ? 'Staff' : 'FirmAdmin';

    try {
      console.log('Updating user role via API:', userId, newRole);
      // Backend expects 'roles' array, not 'role' string
      const response = await api.patch(`/tenants/${selectedTenant.id}/users/${userId}`, { roles: [newRole] });

      if (!response.data.success) {
        throw new Error('Failed to update user');
      }

      console.log('User updated successfully:', response.data.data);

      // Update the user in the local state with the new role structure
      const updatedUsers = tenantUsers.map(u =>
        u.id === userId ? {
          ...u,
          role: newRole, // Keep for backward compatibility
          roles: [newRole] // Update roles array
        } : u
      );

      setTenantUsers(updatedUsers);

      // Update cache
      setTenantDataCache(prev => ({
        ...prev,
        [selectedTenant.id]: {
          ...prev[selectedTenant.id],
          users: updatedUsers
        }
      }));

      toast.success(`${userName}'s role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleDisableUser = async (userId: string) => {
    if (!selectedTenant) return;

    const user = tenantUsers.find(u => u.id === userId);
    if (!user) return;

    // Get the user's display name
    const userName = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';

    // Check current status - handle both formats
    const isCurrentlyActive = user.status === 'ACTIVE' || user.status === 'active';
    // Backend expects UserStatus enum values: 'active', 'invited', 'disabled'
    const newStatus = isCurrentlyActive ? 'disabled' : 'active';

    if (confirm(`Are you sure you want to ${isCurrentlyActive ? 'disable' : 'enable'} ${userName}?`)) {
      try {
        console.log('Updating user status via API:', userId, newStatus);
        const response = await api.patch(`/tenants/${selectedTenant.id}/users/${userId}`, { status: newStatus });

        if (!response.data.success) {
          throw new Error('Failed to update user status');
        }

        console.log('User status updated successfully:', response.data.data.user);

        // Update the user in the local state
        const updatedUsers = tenantUsers.map(u =>
          u.id === userId ? { ...u, status: newStatus } : u
        );

        setTenantUsers(updatedUsers);

        // Update cache
        setTenantDataCache(prev => ({
          ...prev,
          [selectedTenant.id]: {
            ...prev[selectedTenant.id],
            users: updatedUsers
          }
        }));

        toast.success(`${userName} ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      } catch (error) {
        console.error('Error updating user status:', error);
        toast.error('Failed to update user status');
      }
    }
  };

  const handleChangePlan = async (newPlan: string) => {
    if (!selectedTenant) return;

    if (confirm(`Change plan to ${formatPlanName(newPlan)}? This will affect billing and available features.`)) {
      setManagementLoading(true);
      try {
        console.log('Changing plan via API:', { plan: newPlan });
        const response = await api.patch(`/tenants/${selectedTenant.id}/plan`, { plan: newPlan });

        if (!response.data.success) {
          throw new Error('Failed to change plan');
        }

        const result = response.data;
        console.log('Plan changed successfully:', result.data);

        setTenantSettings((prev: any) => ({ ...prev, plan: newPlan }));

        // Update cached billing data
        setTenantDataCache(prev => ({
          ...prev,
          [selectedTenant.id]: {
            ...prev[selectedTenant.id],
            billing: {
              ...prev[selectedTenant.id]?.billing,
              plan: newPlan,
              monthlyCost: result.data.monthlyCost
            }
          }
        }));

        toast.success(`Plan changed to ${formatPlanName(newPlan)}`);
      } catch (error) {
        console.error('Error changing plan:', error);
        toast.error('Failed to change plan');
      } finally {
        setManagementLoading(false);
      }
    }
  };

  const handleUpdatePayment = () => {
    toast('Payment update functionality would integrate with payment processor (Stripe, etc.)', { icon: 'ℹ️' });
  };

  const handleSendSystemMessage = async () => {
    if (!systemMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (!selectedTenant) return;

    setManagementLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://ca.erpca.com/api/v1/tenants/${selectedTenant.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: systemMessage })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const result = await response.json();
      console.log('Message sent successfully:', result.data);

      // Update cache with new message
      setTenantDataCache(prev => ({
        ...prev,
        [selectedTenant.id]: {
          ...prev[selectedTenant.id],
          support: {
            ...prev[selectedTenant.id]?.support,
            systemMessages: [
              result.data.message,
              ...(prev[selectedTenant.id]?.support?.systemMessages || [])
            ]
          }
        }
      }));

      toast.success(`System message sent to ${result.data.message.recipients} users`);
      setSystemMessage('');
      setShowMessageModal(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setManagementLoading(false);
    }
  };

  const handleResetPasswords = async () => {
    if (!selectedTenant) return;

    if (confirm('Reset passwords for all users in this tenant? They will receive email instructions.')) {
      setManagementLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`https://ca.erpca.com/api/v1/tenants/${selectedTenant.id}/reset-passwords`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to reset passwords');
        }

        const result = await response.json();
        console.log('Passwords reset successfully:', result.data);

        toast.success(result.data.message);
      } catch (error) {
        console.error('Error resetting passwords:', error);
        toast.error('Failed to reset passwords');
      } finally {
        setManagementLoading(false);
      }
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedTenant) return;

    // Note: Support report endpoint is not yet implemented
    toast.info('Support report generation feature is coming soon!');
  };

  const handleAccessLogs = () => {
    toast('Opening tenant logs in new window...', { icon: 'ℹ️' });
    // In real app, this would open a logs viewer
    window.open(`/admin/logs/${selectedTenant?.id}`, '_blank');
  };

  const handleSaveSettings = async () => {
    if (!selectedTenant) return;

    setManagementLoading(true);
    try {
      console.log('Saving tenant settings:', tenantSettings);
      const response = await api.patch(`/tenants/${selectedTenant.id}/settings`, tenantSettings);

      if (!response.data.success) {
        throw new Error('Failed to save settings');
      }

      console.log('Settings saved successfully:', response.data.data);

      // Update the tenant in the main list
      setTenants(prev =>
        prev.map(t =>
          t.id === selectedTenant.id
            ? { ...t, ...response.data.data.tenant }
            : t
        )
      );

      // Update selected tenant
      setSelectedTenant({ ...selectedTenant, ...response.data.data.tenant });

      toast.success('Settings saved successfully');
      fetchTenants(); // Refresh the list
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.message || error.message));
    } finally {
      setManagementLoading(false);
    }
  };

  const handleUpdateTenantAdminCredentials = async (data: {
    loginId?: string;
    password?: string;
    firstName?: string;
    lastName?: string
  }) => {
    if (!selectedTenant) return;

    setManagementLoading(true);
    try {
      console.log('Updating tenant admin credentials:', data);

      const response = await api.patch(`/platform/tenants/${selectedTenant.id}/admin`, data);

      if (response.data.success) {
        toast.success('Admin credentials updated successfully');
      } else {
        throw new Error(response.data.message || 'Failed to update credentials');
      }
    } catch (error: any) {
      console.error('Error updating admin credentials:', error);
      if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update admin credentials');
      }
      throw error; // Re-throw so the component can handle it
    } finally {
      setManagementLoading(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;

    const tenantName = selectedTenant.name;

    if (!confirm(`Are you sure you want to DELETE "${tenantName}"? This action cannot be undone and will permanently delete all data.`)) {
      return;
    }

    const confirmText = prompt('Type "DELETE" to confirm this destructive action:');
    if (confirmText !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }

    setManagementLoading(true);
    try {
      console.log('Deleting tenant:', selectedTenant.id);
      const response = await api.delete(`/tenants/${selectedTenant.id}`);

      if (!response.data.success) {
        throw new Error('Failed to delete tenant');
      }

      console.log('Tenant deleted successfully:', response.data);

      // Remove tenant from local state
      setTenants(prev => prev.filter(t => t.id !== selectedTenant.id));
      setShowManageModal(false);
      toast.success('Tenant deleted successfully');
      fetchTenants(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting tenant:', error);
      toast.error('Failed to delete tenant: ' + (error.response?.data?.message || error.message));
    } finally {
      setManagementLoading(false);
    }
  };

  const handleSuspendTenant = async (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    const tenantName = tenant?.name || 'this tenant';

    if (!confirm(`Are you sure you want to suspend "${tenantName}"? This will prevent all users from accessing their account.`)) {
      return;
    }

    try {
      setActionLoading(tenantId);
      console.log('Suspending tenant:', tenantId);
      console.log('API headers:', api.defaults.headers.common);

      const response = await api.post(`/tenants/${tenantId}/suspend`, {
        reason: 'Suspended by SuperAdmin',
      });

      console.log('Suspend response:', response.data);

      if (response.data.success) {
        toast.success('Tenant suspended successfully');
        fetchTenants();
      } else {
        toast.error('Failed to suspend tenant: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error suspending tenant:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMsg = error.response?.data?.message || error.message || 'Failed to suspend tenant';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivateTenant = async (tenantId: string) => {
    try {
      setActionLoading(tenantId);
      console.log('Reactivating tenant:', tenantId);
      console.log('API headers:', api.defaults.headers.common);

      const response = await api.post(`/tenants/${tenantId}/reactivate`);

      console.log('Reactivate response:', response.data);

      if (response.data.success) {
        toast.success('Tenant reactivated successfully');
        fetchTenants();
      } else {
        toast.error('Failed to reactivate tenant: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error reactivating tenant:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMsg = error.response?.data?.message || error.message || 'Failed to reactivate tenant';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || tenant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Section Components
  const DashboardSection = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Firms</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Trial</p>
              <p className="text-2xl font-bold text-gray-900">{stats.trial}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-gray-900">{stats.suspended}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">MRR</p>
              <p className="text-2xl font-bold text-gray-900">${stats.revenue.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowTenantModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Firm
          </button>
          <button
            onClick={() => setActiveSection('tenants')}
            className="flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Building2 className="w-5 h-5 mr-2" />
            Manage Firms
          </button>
          <button
            onClick={() => setActiveSection('analytics')}
            className="flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            View Analytics
          </button>
        </div>
      </div>
    </div>
  );

  const TenantsSection = () => (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search tenants by name or subdomain..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <button
                onClick={fetchTenants}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Tenants</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTenantModal(true)}
              className="px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Tenant
            </button>
            <button
              onClick={fetchTenants}
              disabled={loading}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tenants...</p>
          </div>
        ) : filteredTenants.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No tenants found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                        <div className="text-sm text-gray-500">{tenant.subdomain}.erpca.ca</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        planColors[tenant.plan?.toLowerCase() as keyof typeof planColors] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {formatPlanName(tenant.plan)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusColors[tenant.status?.toLowerCase() as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {formatStatusName(tenant.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.userCount || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleManageTenant(tenant)}
                          className="text-primary-600 hover:text-primary-900 flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Manage
                        </button>
                        <TenantActions
                          tenantId={tenant.id}
                          tenantName={tenant.name}
                          status={tenant.status}
                          onView={() => handleViewTenant(tenant)}
                          onManage={() => handleManageTenant(tenant)}
                          onSuspend={handleSuspendTenant}
                          onReactivate={handleReactivateTenant}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const AnalyticsSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Analytics & Reports</h2>
        <p className="text-gray-600">Platform analytics and reporting features will be available here.</p>
      </div>
    </div>
  );

  const UsersSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Users</h2>
        <p className="text-gray-600">Platform user management features will be available here.</p>
      </div>
    </div>
  );

  const PlansSection = () => <PlanList hideHeader={true} />;

  const SettingsSection = () => <PlatformSettings />;

  const SecuritySection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Security & Logs</h2>
        <p className="text-gray-600">Security settings and audit logs will be available here.</p>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardSection />;
      case 'tenants':
        return <TenantsSection />;
      case 'workflows':
        return <CountryWorkflowManagement />;
      case 'plans':
        return <PlansSection />;
      case 'analytics':
        return <AnalyticsSection />;
      case 'users':
        return <UsersSection />;
      case 'settings':
        return <SettingsSection />;
      case 'notification-config':
        return <PlatformNotificationConfigManagement />;
      case 'security':
        return <SecuritySection />;
      default:
        return <DashboardSection />;
    }
  };

  const hasAdminAccess = user?.roles?.some(role =>
    ['SuperAdmin', 'SUPER_ADMIN', 'PLATFORM_ADMIN'].includes(role)
  );

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Building2 className="w-8 h-8 text-primary-500 mr-3" />
            <div>
              <h1 className="text-lg font-bold text-gray-900">ERPCA</h1>
              <p className="text-sm text-gray-600">Super Admin</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigationItems.map((item) => (
            <NavigationItem
              key={item.id}
              id={item.id}
              icon={item.icon}
              label={item.label}
              isActive={activeSection === item.id}
              onClick={() => setActiveSection(item.id)}
            />
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white font-semibold text-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">Super Admin</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Debug Info */}
        <div className="bg-blue-50 border-b border-blue-200 p-2">
          <div className="px-4 text-xs text-blue-700 flex justify-between items-center">
            <div>
              <strong>Debug:</strong> User: {user?.email} | Roles: {user?.roles?.join(', ')} |
              Auth: {isAuthenticated ? 'Yes' : 'No'} | Tenant: {tenant?.subdomain}
            </div>
            <button
              onClick={() => {
                console.log('Manual API test triggered');
                fetchTenants();
              }}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
            >
              Test API
            </button>
          </div>
        </div>

        {/* Page Header */}
        <div className="bg-white shadow-sm border-b px-6 py-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {navigationItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
            </h1>
            <p className="text-gray-600 mt-1">
              {activeSection === 'dashboard' && 'Overview of platform metrics and quick actions'}
              {activeSection === 'tenants' && 'Manage all firm accounts and settings'}
              {activeSection === 'workflows' && 'Manage country-specific workflow templates'}
              {activeSection === 'plans' && 'Manage subscription plans and pricing'}
              {activeSection === 'analytics' && 'Platform analytics and reporting'}
              {activeSection === 'users' && 'Manage platform users and permissions'}
              {activeSection === 'settings' && 'Configure platform settings'}
              {activeSection === 'notification-config' && 'Configure platform-wide notification settings'}
              {activeSection === 'security' && 'Security settings and audit logs'}
            </p>
          </div>
          <div className="pt-1">
            <NotificationBell />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderActiveSection()}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showTenantModal && !selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Create New Firm</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const tenantData = {
                name: formData.get('name') as string,
                subdomain: formData.get('subdomain') as string,
                plan: formData.get('plan') as string,
                adminEmail: formData.get('adminEmail') as string,
                adminFirstName: formData.get('adminFirstName') as string,
                adminLastName: formData.get('adminLastName') as string,
              };
              console.log('Creating tenant:', tenantData);
              toast.info('Tenant creation feature coming soon!');
              setShowTenantModal(false);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                  <input
                    name="subdomain"
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter subdomain"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    name="plan"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                  <input
                    name="adminEmail"
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="admin@company.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      name="adminFirstName"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      name="adminLastName"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Create Firm
                </button>
                <button
                  type="button"
                  onClick={() => setShowTenantModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tenant Details Modal */}
      {showTenantModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Tenant Details</h3>
              <button
                onClick={() => setShowTenantModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <p className="text-sm text-gray-900">{selectedTenant.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subdomain</label>
                  <p className="text-sm text-gray-900">{selectedTenant.subdomain}.erpca.ca</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Plan</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    planColors[selectedTenant.plan?.toLowerCase() as keyof typeof planColors] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {formatPlanName(selectedTenant.plan)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    statusColors[selectedTenant.status?.toLowerCase() as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {formatStatusName(selectedTenant.status)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Users</label>
                  <p className="text-sm text-gray-900">{selectedTenant.userCount || 1}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-900">{new Date(selectedTenant.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowTenantModal(false);
                    handleManageTenant(selectedTenant);
                  }}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Manage Tenant
                </button>
                <button
                  onClick={() => setShowTenantModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Management Modal */}
      {console.log('Modal render check:', { showManageModal, selectedTenant: !!selectedTenant })}
      {showManageModal && selectedTenant && (
        <TenantManageModal
          isOpen={showManageModal}
          onClose={() => setShowManageModal(false)}
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
          isPlatformTenant={false}
          onTenantUpdate={() => {
            // Refresh tenant list after updates
            fetchTenants();
          }}
        />
      )}
    </div>
  );
};

export default SuperAdminDashboard;
